// API publique en fichiers statiques, écrits au build par scripts/prerender.mjs (rien à servir
// côté serveur, aucun quota) :
//   /api/models.json         la liste des modèles, avec le lien de chaque fiche
//   /api/models/<slug>.json  la fiche d'un modèle : poids par format, cache KV par contexte, GPU
//                            nécessaires, fichiers GGUF publiés, moteurs, vérifications
//   /api/gpus.json           les GPU du calculateur (mémoire, bande passante, calcul)
//   /badge/<slug>.svg        badge « VRAM » pour la fiche Hugging Face d'un modèle
// Tout vient du moteur (llmVram.ts) et du catalogue de la veille : les chiffres sont ceux du site.

import { GiB, GPUS, MODELS, SHEET_CONTEXT, modelSheet, type ModelPreset } from "../pages/llmVram";
import { CATALOG_SNAPSHOT, labOf, runsWith } from "../pages/catalog";
import { countryName } from "../pages/labs";
import { modelName, modelPath, modelSlug } from "./vramModels";
import { SITE } from "./routes";

const round = (x: number, d = 2) => Math.round(x * 10 ** d) / 10 ** d;

/** GPU d'une seule carte où le modèle tient en 4-bit (le plus petit), sinon le nombre de H100. */
function fitLine(m: ModelPreset) {
  const s = modelSheet(m);
  const q4 = s.formats.findIndex((f) => f.id === "q4_k_m");
  const one = ["rtx-4090", "rtx-5090", "rtx-pro-6000", "h100", "h200", "b200"]
    .map((id) => s.gpus.find((g) => g.id === id)!)
    .find((g) => g.counts[q4] === 1);
  if (one) return `1× ${one.label.replace(/^(NVIDIA|GeForce) /, "")}`;
  const h100 = s.gpus.find((g) => g.id === "h100")!.counts[q4];
  return h100 === null ? "over 128× H100" : `${h100}× H100`;
}

function modelJson(m: ModelPreset) {
  const s = modelSheet(m);
  const entry = CATALOG_SNAPSHOT.models[m.id];
  const lab = labOf(m);
  const rw = runsWith(entry?.engines);
  return {
    id: m.id,
    name: modelName(m),
    repo: m.repo || null,
    lab: lab ? { name: lab.name, country: lab.country ? countryName(lab.country) : null } : null,
    released: m.released ?? null,
    parameters: m.params,
    activeParameters: m.activeParams ?? null,
    maxContext: Number.isFinite(m.maxContext) ? m.maxContext : null,
    architecture: m.arch,
    page: `${SITE}${modelPath(m)}`,
    calculator: `${SITE}/llm-vram-calculator?model=${m.id}`,
    weightsGiB: Object.fromEntries(s.formats.map((f) => [f.id, round(f.weightsGiB)])),
    kvCacheGiBPerRequest: s.kv.map((k) => ({ context: k.ctx, bf16: round(k.bf16GiB, 3), fp8: round(k.fp8GiB, 3) })),
    recurrentStateGiBPerRequest: s.stateGiB ? round(s.stateGiB, 4) : 0,
    gpusNeeded: {
      scenario: `one request of ${SHEET_CONTEXT} tokens, BF16 KV cache; vLLM on data-center GPUs, llama.cpp elsewhere`,
      gpus: s.gpus.map((g) => ({ gpu: g.id, name: g.label, memoryGB: g.vram, engine: g.engine, byFormat: Object.fromEntries(s.formats.map((f, i) => [f.id, g.counts[i]])) })),
    },
    ggufFiles: entry?.ggufFiles
      ? { repo: entry.ggufFiles.repo, gib: Object.fromEntries(Object.entries(entry.ggufFiles.files).map(([q, b]) => [q, round(b / GiB)])) }
      : null,
    runsWith: rw?.names ?? [],
    verified: entry?.verified ?? null,
    updated: CATALOG_SNAPSHOT.updated,
  };
}

// Badge : même gabarit que shields.io (20 px de haut, Verdana 11), aux couleurs du site.
const CHAR = (c: string) => (/[il.,:;|!' ]/.test(c) ? 3.6 : /[mwMW]/.test(c) ? 9.2 : /[A-Z0-9×]/.test(c) ? 7.4 : 6.4);
const textWidth = (t: string) => [...t].reduce((w, c) => w + CHAR(c), 0);
// Guillemets aussi : le texte va dans aria-label="…" (un « " » y ouvrait un attribut, donc un script dans le SVG).
const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Largeur du badge en pixels (la même que dans le SVG) : la page qui l'affiche réserve sa place. */
export const badgeWidth = (label: string, value: string) => Math.round(textWidth(label) + 12) + Math.round(textWidth(value) + 12);

export function badgeSvg(label: string, value: string) {
  const lw = Math.round(textWidth(label) + 12), vw = Math.round(textWidth(value) + 12), w = lw + vw;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="20" role="img" aria-label="${esc(`${label}: ${value}`)}">` +
    `<title>${esc(`${label}: ${value}`)}</title>` +
    `<clipPath id="r"><rect width="${w}" height="20" rx="3"/></clipPath>` +
    `<g clip-path="url(#r)"><rect width="${lw}" height="20" fill="#0b1220"/><rect x="${lw}" width="${vw}" height="20" fill="#49f2c2"/></g>` +
    `<g font-family="Verdana,DejaVu Sans,sans-serif" font-size="11" text-anchor="middle">` +
    `<text x="${lw / 2}" y="14" fill="#f4f7ff">${esc(label)}</text>` +
    `<text x="${lw + vw / 2}" y="14" fill="#04121a">${esc(value)}</text></g></svg>\n`;
}

/** Valeur du badge d'un modèle : poids en 4-bit et le plus petit matériel où il tient. */
export const badgeValue = (m: ModelPreset) => {
  const q4 = modelSheet(m).formats.find((f) => f.id === "q4_k_m")!.weightsGiB;
  return `${q4 >= 100 ? q4.toFixed(0) : q4.toFixed(1)} GiB in 4-bit · ${fitLine(m)}`;
};

/** Chemin publié → contenu, pour scripts/prerender.mjs. */
export function apiFiles(): Record<string, string> {
  const files: Record<string, string> = {};
  const json = (x: unknown) => `${JSON.stringify(x, null, 1)}\n`;
  for (const m of MODELS) {
    files[`api/models/${modelSlug(m)}.json`] = json(modelJson(m));
    files[`badge/${modelSlug(m)}.svg`] = badgeSvg("VRAM", badgeValue(m));
  }
  files["api/models.json"] = json({
    source: `${SITE}/llm-vram-calculator`,
    updated: CATALOG_SNAPSHOT.updated,
    models: MODELS.map((m) => ({ id: m.id, name: modelName(m), repo: m.repo || null, parameters: m.params, page: `${SITE}${modelPath(m)}`, api: `${SITE}/api/models/${modelSlug(m)}.json` })),
  });
  files["api/gpus.json"] = json({
    source: `${SITE}/llm-vram-calculator`,
    gpus: GPUS.map((g) => ({ id: g.id, name: g.label, group: g.group, memoryGB: g.vram, bandwidthTBs: g.bandwidth, bf16Tflops: g.tflops, fp8: g.fp8, fp4: g.fp4 })),
  });
  return files;
}
